/*
 * TLS cart drawer: own replacement for the Upcart app.
 * - Opens after every add-to-cart (theme form, Kaching, sticky bars, other apps): fetch + XHR to /cart/add are watched.
 * - Opens on the header cart icon instead of going to /cart.
 * - Line items, totals and discounts are rendered by sections/tls-cart.liquid (Section Rendering API).
 * - Upsells (max 10, nothing that is already in the cart): first "vaak samen gekocht" from past orders (rendered by the
 *   section from metafield tls_cart.bought_together, so it shows instantly), then Shopify "related" recommendations.
 * - Adds from the drawer get the hidden line property _bron = cart-upsell:fbt | cart-upsell:related | cart-empty:...
 *   so their revenue (and which source works best) can be measured in orders.
 * - Optional "cart reserved" countdown bar (25 Oct 2026, NL/DE November 2026): TimerCore (pure, unit-tested) + the
 *   tlsc-timer-ui block; display only, it never changes the cart.
 * - 26 Oct 2026 (README "26 okt 2026: lade-reparaties + bundelknop"): every cart request has a time limit and a friendly
 *   translated error (NetCore); the checkout button waits (max 3 s) for a cart change that is still on its way; undo after
 *   removing a line (CartCore); bundle button to the next Kaching tier from the price list snippets/tls-cart-bundles.liquid
 *   (BundleCore, the price is checked against what the cart really charges). Pure blocks are unit-tested by
 *   cart/tls_cart_unit.test.js.
 */
(function () {
  'use strict';
  if (window.TLSCart) return;

  var SECTION = 'tls-cart';
  var OPTOUT = '_tls_protection_optout';   // hidden cart attribute: customer switched shipping protection off
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var nativeFetch = window.fetch.bind(window);
  var el = null;
  var T = {};
  var moneyFormat = '{{amount_with_comma_separator}}';
  var state = { open: false, cart: null, recCache: {}, products: {}, lastFocus: null, openTimer: null, busyCount: 0,
    stale: false,        // tab was hidden / restored from back-forward cache: state.cart may miss changes made elsewhere
    mut: 0,              // counts cart changes sent from here, so an older background read never overwrites a newer one
    protPending: null,   // the one protection add in flight (shared, never two at once)
    protFixing: false,   // a protection repair is running (one at a time, cannot loop)
    waitAdd: false,      // drawer opened from the icon while an add-to-cart was still on its way
    leaving: 0,          // checkout clicked while a cart change was on its way: id of that wait (max 3 s), 0 = none
    fails: 0,            // cart changes from here that failed with a message: checkout does not go on after one (F2)
    failAt: 0,           // when the last one failed (ms)
    failShown: 0 };      // fails at the last checkout tap that was held for a failure (the next tap goes on)
  var LIMIT = 8000;      // ms: every cart / section / recommendations request of the drawer gives up after this

  // ---------- helpers ----------
  function $(sel, ctx) { return (ctx || el).querySelector(sel); }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function formatMoney(cents) {
    // the shop's money format, the same as every other amount in the drawer (bundle button included since 27 Oct: NL
    // "49,95", DE "49,95 €"); self-contained on purpose (tests_money_format.js cuts this function out of the file)
    var fmt = moneyFormat;
    cents = Number(cents || 0);
    function delim(n, prec, th, dec) {
      var parts = (n / 100).toFixed(prec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return parts.join(dec);
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, k) {
      switch (k) {
        case 'amount': return delim(cents, 2, ',', '.');
        case 'amount_no_decimals': return delim(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return delim(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return delim(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return delim(cents, 2, "'", '.');
        case 'amount_with_space_separator': return delim(cents, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return delim(cents, 0, ' ', ',');
        case 'amount_with_period_and_space_separator': return delim(cents, 2, ' ', '.');
        default: return delim(cents, 2, '.', ',');
      }
    });
  }
  function imgUrl(src, width) {
    if (!src) return '';
    if (typeof src === 'object') src = src.src || '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&])width=\d+/, '$1width=' + width);
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }
  function pageProductId() {
    try { return window.ShopifyAnalytics.meta.product.id; } catch (e) { return null; }
  }

  /* tlsc-timer-core:start
   * "Cart reserved" countdown, pure logic (no DOM, no network, no cart writes). Unit-tested by
   * timer/tls_timer_unit.test.js, which runs exactly this block. Stored value: {s: start ms, sig: real cart lines}. */
  var TimerCore = (function () {
    // EU summer time: last Sunday of March 01:00 UTC until the last Sunday of October 01:00 UTC
    function lastSunday(year, month) {
      var d = new Date(Date.UTC(year, month + 1, 0, 1, 0, 0));
      d.setUTCDate(d.getUTCDate() - d.getUTCDay());
      return d.getTime();
    }
    function amsterdamOffset(ms) {   // minutes ahead of UTC at this instant (Europe/Amsterdam)
      var y = new Date(ms).getUTCFullYear();
      return ms >= lastSunday(y, 2) && ms < lastSunday(y, 9) ? 120 : 60;
    }
    var WHEN = /^\s*(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2})(?::(\d{2}))?)?\s*(Z|[+-]\d{2}:?\d{2})?\s*$/i;
    // '2026-11-01 00:00' (Dutch time), '2026-11-30' (whole day) or with an explicit offset ('...T00:00:00+01:00', 'Z')
    // -> {at: ms, span: how long the written value lasts (day / minute / second)}; null when unreadable
    function parseWhen(str) {
      var m = WHEN.exec(String(str == null ? '' : str));
      if (!m) return null;
      var y = +m[1], mo = +m[2] - 1, d = +m[3];
      var h = m[4] != null ? +m[4] : 0, mi = m[5] != null ? +m[5] : 0, s = m[6] != null ? +m[6] : 0;
      if (mo < 0 || mo > 11 || d < 1 || h > 23 || mi > 59 || s > 59) return null;
      var wall = Date.UTC(y, mo, d, h, mi, s);
      if (new Date(wall).getUTCDate() !== d) return null;   // 31 Nov and the like
      var span = m[4] == null ? 86400000 : m[6] == null ? 60000 : 1000;
      var at;
      if (m[7]) {
        var off = 0;
        if (m[7].toUpperCase() !== 'Z') {
          var dg = m[7].slice(1).replace(':', '');
          off = (m[7].charAt(0) === '-' ? -1 : 1) * (+dg.slice(0, 2) * 60 + +dg.slice(2, 4));
        }
        at = wall - off * 60000;
      } else {
        at = wall - 60 * 60000;                                   // winter time (CET) ...
        if (amsterdamOffset(at) === 120) at = wall - 120 * 60000; // ... or summer time (CEST)
      }
      return { at: at, span: span };
    }
    // start <= now < end (the end minute / day included); unreadable or empty = never
    function inWindow(now, start, end) {
      var a = parseWhen(start), b = parseWhen(end);
      return !!(a && b) && now >= a.at && now < b.at + b.span;
    }
    // the real products in the cart (the shipping protection line never counts), as a stable text
    function signature(cart, protId) {
      return ((cart && cart.items) || []).filter(function (i) {
        return !protId || String(i.product_id) !== String(protId);
      }).map(function (i) { return i.key + '*' + i.quantity; }).sort().join('|');
    }
    function valid(st) {
      return !!(st && typeof st === 'object' && typeof st.s === 'number' && isFinite(st.s) && typeof st.sig === 'string');
    }
    // One decision. o = {sig, now, dur, active, visible (drawer open), prevSig (this page's previous sig, null = none yet),
    // prevView, leftMs (test override or null)}. Returns {store (null = remove), write, view: none|run|expired, left,
    // started, say: start|expired|null, usedLeft}.
    function step(stored, o) {
      var st = valid(stored) ? { s: stored.s, sig: stored.sig } : null;
      var r = { store: null, write: false, view: 'none', left: 0, started: false, say: null, usedLeft: false };
      if (!o.active || !o.sig) {
        // outside the window / switched off / empty cart (protection alone counts as empty): no timer shown. An empty
        // cart removes the stored timer; outside the window only a finished (or unreadable) one is removed, so a tab in
        // the test mode (or still on older settings) and a tab outside the window never undo each other's write.
        var running = !!st && st.s <= o.now && o.now < st.s + o.dur;
        r.write = stored != null && (!o.sig || !running);
        return r;
      }
      if (o.leftMs != null) {
        var left = Math.max(0, Math.min(o.dur, o.leftMs));
        st = { s: o.now - (o.dur - left), sig: o.sig };
        r.started = r.usedLeft = true;
      } else if (!st) {
        // first add (the cart changed on this page) or first time the customer sees the drawer with products in it
        var changed = o.prevSig != null && o.prevSig !== o.sig;
        if (!o.visible && !changed) return r;
        st = { s: o.now, sig: o.sig };
        r.started = true;
      } else {
        if (st.s > o.now) st.s = o.now;   // clock moved back: never more than the full time
        if (st.sig !== o.sig) {
          if (o.now >= st.s + o.dur) { st = { s: o.now, sig: o.sig }; r.started = true; }   // expired + cart changed: new 10:00
          else st = { s: st.s, sig: o.sig };   // still running: keep counting from the first add
        }
      }
      r.store = st;
      r.write = !valid(stored) || stored.s !== st.s || stored.sig !== st.sig;
      r.left = Math.max(0, st.s + o.dur - o.now);
      r.view = r.left > 0 ? 'run' : 'expired';
      if (r.view === 'run' && r.started) r.say = 'start';
      else if (r.view === 'expired' && (o.prevView === 'run' || r.started)) r.say = 'expired';
      return r;
    }
    // 599001 ms -> '10:00', 598000 -> '09:58', 0 -> '00:00' (whole seconds rounded up, like every countdown)
    function fmt(ms) {
      var t = Math.max(0, Math.ceil(ms / 1000));
      var m = Math.floor(t / 60), s = t % 60;
      return (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;
    }
    // ms until the shown second changes (+30 ms), so one 1-second interval stays on the boundary
    function untilNextSecond(leftMs) {
      return ((leftMs % 1000) + 1000) % 1000 + 30;
    }
    // ?tls_timer_test=1 forces the date window for this tab (sessionStorage), =0 stops it; &tls_timer_left=<seconds>
    // (only with the test on) sets the time left once on this page load
    function testParams(search, sessionFlag) {
      var q = String(search || '');
      var t = /[?&]tls_timer_test=([^&#]*)/.exec(q);
      var flag = t ? (t[1] === '1' ? '1' : '0') : null;
      var test = flag ? flag === '1' : sessionFlag === '1';
      var l = /[?&]tls_timer_left=(\d{1,5})(?=&|#|$)/.exec(q);
      return { test: test, flag: flag, leftMs: test && l ? parseInt(l[1], 10) * 1000 : null };
    }
    function fill(tpl) {   // [before, after] around [time]; a text without [time] gets the time at the end
      tpl = String(tpl == null ? '' : tpl);
      var i = tpl.indexOf('[time]');
      return i < 0 ? [tpl + ' ', ''] : [tpl.slice(0, i), tpl.slice(i + 6)];
    }
    return { parseWhen: parseWhen, inWindow: inWindow, signature: signature, step: step, fmt: fmt,
      untilNextSecond: untilNextSecond, testParams: testParams, fill: fill, amsterdamOffset: amsterdamOffset };
  })();
  /* tlsc-timer-core:end */

  /* tlsc-net-core:start
   * F2 (26 Oct 2026): every cart / section / recommendations request of the drawer has a time limit, and every failure
   * becomes ONE short translated message (never raw exception text such as "Unexpected token '<'"). Pure logic,
   * unit-tested by cart/tls_cart_unit.test.js (env injects fetch-free timers / AbortController there). */
  var NetCore = (function () {
    function noop() {}
    // -> Promise of {status, ok, data (parsed JSON or null), json (body was valid JSON)}; rejects with err.kind =
    // 'timeout' (limit reached, request aborted) or 'network' (no answer at all). The limit covers the body as well.
    function request(fetchFn, url, opts, ms, env) {
      env = env || {};
      var setT = env.setTimeout || setTimeout, clearT = env.clearTimeout || clearTimeout;
      var AC = 'AbortController' in env ? env.AbortController : (typeof AbortController === 'function' ? AbortController : null);
      var ctl = null;
      try { ctl = AC ? new AC() : null; } catch (e) { ctl = null; }
      var o = {};
      for (var k in (opts || {})) if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k];
      if (ctl) o.signal = ctl.signal;
      return new Promise(function (resolve, reject) {
        var over = false;
        function fail(kind, e) {
          if (over) return;
          over = true;
          clearT(t);
          var err = new Error(kind);
          err.kind = kind;
          err.cause = e;
          reject(err);
        }
        var t = setT(function () {
          try { if (ctl) ctl.abort(); } catch (e) { /* ignore */ }
          fail('timeout');
        }, ms);
        var p;
        try { p = Promise.resolve(fetchFn(url, o)); } catch (e) { fail('network', e); return; }
        p.then(function (r) {
          return Promise.resolve(r.text()).then(function (txt) {
            if (over) return;
            var data = null, json = true;
            try { data = txt ? JSON.parse(txt) : null; } catch (e) { json = false; }
            over = true;
            clearT(t);
            resolve({ status: r.status, ok: !!r.ok, data: data, json: json });
          });
        }).then(noop, function (e) { fail('network', e); });
      });
    }
    // a response that is not a usable JSON answer -> an Error with .kind: 'busy' (429 / 5xx: Shopify is overloaded),
    // 'shop' (422 with Shopify's own text, kept in .shopText for debugging only) or 'error' (anything else, e.g. HTML)
    function failure(res) {
      var e = new Error('cart request failed');
      e.status = res.status;
      if (res.status === 429 || res.status >= 500) e.kind = 'busy';
      else if (res.status === 422 && res.data && typeof res.data.description === 'string') { e.kind = 'shop'; e.shopText = res.data.description; }
      else e.kind = 'error';
      return e;
    }
    // the text the customer sees: always one of OUR translated texts (Shopify's own error texts are often English, e.g.
    // "Cannot find variant" in the NL shop, checked 26 Oct); busy / too slow gets its own "try again in a moment"
    function message(err, T) {
      T = T || {};
      var kind = err && err.kind;
      if (kind === 'timeout' || kind === 'busy') return T.slow || T.error;
      return T.error;
    }
    // true only when Shopify clearly REFUSED the request, so nothing of it was saved: 422 ('shop'), or another 4xx
    // answer except 429. A timeout / no answer / 429 / 5xx / a 200 that is not JSON may well have gone through: never
    // send such a request again (an add would then put the product in the cart twice; review 27 Oct)
    function refused(err) {
      if (!err) return false;
      if (err.kind === 'shop') return true;
      return err.kind === 'error' && err.status >= 400 && err.status < 500 && err.status !== 429;
    }
    // the cart changes that are still on their way (checkout waits for them, max a few seconds). env.fails = the drawer's
    // counter of failed changes. When the FIRST change of a busy stretch starts, its value is taken as the baseline for a
    // checkout tap (base()): a change that already failed before the tap but is still on its way (its drawer re-read runs)
    // still counts. Review 27 Oct: the baseline was taken at the tap, so such a failure was missed and checkout went on
    // with the insurance the customer had just switched off.
    function Pending(env) {
      env = env || {};
      var setT = env.setTimeout || setTimeout, clearT = env.clearTimeout || clearTimeout;
      var fails = env.fails || function () { return 0; };
      var list = [];
      var base = 0;
      return {
        add: function (p) {
          if (!list.length) base = fails();   // a new busy stretch: failures from here on hold a checkout tap
          list.push(p);
          function rm() { var i = list.indexOf(p); if (i >= 0) list.splice(i, 1); }
          p.then(rm, rm);
          return p;
        },
        count: function () { return list.length; },
        base: function () { return list.length ? base : fails(); },
        // after a "stay": the failures so far have been shown, the next tap only waits for new ones
        rebase: function () { base = fails(); },
        // true when every change on its way (also ones started meanwhile) has finished; false when ms ran out first
        settle: function (ms) {
          return new Promise(function (resolve) {
            var over = false;
            var t = setT(function () { over = true; resolve(false); }, ms);
            (function wait() {
              if (over) return;
              if (!list.length) { over = true; clearT(t); resolve(true); return; }
              Promise.all(list.map(function (p) { return p.then(noop, noop); })).then(wait);
            })();
          });
        }
      };
    }
    // checkout tapped while changes are on their way: wait for them (max ms). -> Promise of true = go on to the checkout
    // (everything went through, or the limit ran out with requests still running: the agreed 3 s rule); false = stay,
    // because a change of this busy stretch failed - before the tap (still being re-read) or while waiting (e.g.
    // switching the protection off got a 429: the cart still charges it). The customer then sees the error and the
    // re-read drawer first; the next tap goes through. fails() = failed changes so far.
    function leave(pend, fails, ms) {
      var before = pend.base ? pend.base() : fails();
      return pend.settle(ms).then(function () {
        if (fails() === before) return true;
        if (pend.rebase) pend.rebase();
        return false;
      });
    }
    // a failed change the customer may not have seen yet: counted after the last time a tap was held for a failure
    // (shown) and less than ms ago. Review 27 Oct (DE preview): a 429 at 80 ms with a quick re-read left nothing on its
    // way at a tap at 300 ms, so the plain link went on at once - such a tap is held once as well. Round 4 (NL): the same
    // with a '+' tapped after the failure: that newer change started a new busy stretch whose baseline already held the
    // failure, so the wait went on to the checkout. checkoutClick now holds a fresh failure ALWAYS (also while changes are
    // on their way), before it looks at anything else.
    function fresh(now, failAt, fails, shown, ms) {
      return fails !== shown && now - failAt < ms;
    }
    return { request: request, failure: failure, message: message, refused: refused, Pending: Pending, leave: leave, fresh: fresh };
  })();
  /* tlsc-net-core:end */

  /* tlsc-cart-core:start
   * F1 totals (the same sums the section prints) and F4 undo (what is needed to put a removed line back exactly as it
   * was). Pure, unit-tested by cart/tls_cart_unit.test.js. */
  var CartCore = (function () {
    // subtotal = sum of the line prices after line discounts; order = discounts on the whole order;
    // subtotal - order must be the checkout amount (cart.total_price)
    function totals(cart) {
      var items = (cart && cart.items) || [];
      var sub = 0, order = 0;
      items.forEach(function (i) { sub += Number(i.final_line_price) || 0; });
      ((cart && cart.cart_level_discount_applications) || []).forEach(function (a) { order += Number(a.total_allocated_amount) || 0; });
      var total = Number(cart && cart.total_price) || 0;
      return { subtotal: sub, order: order, total: total, ok: sub - order === total };
    }
    // a removed line -> the cart/add.js item that brings it back (same variant, quantity, ALL properties incl. the
    // hidden ones such as _bron / __kaching_bundles, same subscription plan); null when there is nothing to undo
    function snapshot(line, now) {
      if (!line || !line.variant_id || !(line.quantity > 0)) return null;
      var props = {};
      var src = line.properties || {};
      for (var k in src) if (Object.prototype.hasOwnProperty.call(src, k) && src[k] != null && src[k] !== '') props[k] = src[k];
      var it = { id: Number(line.variant_id), quantity: Number(line.quantity) };
      if (Object.keys(props).length) it.properties = props;
      var sp = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
      if (sp && sp.id) it.selling_plan = Number(sp.id);
      return { item: it, at: now, title: line.product_title || line.title || '' };
    }
    function undoable(snap, now, ms) {
      return !!(snap && snap.item && now - snap.at < ms);
    }
    // shipping protection default-on: a real product in the cart (or about to be added: assumeReal), no protection line
    // yet, and the customer did not switch it off for this cart (cart attribute optKey = '1')
    function needsProt(cart, protId, assumeReal, optKey) {
      if (!protId) return false;
      var items = (cart && cart.items) || [];
      var hasProt = items.some(function (i) { return String(i.product_id) === String(protId); });
      var hasReal = !!assumeReal || items.some(function (i) { return String(i.product_id) !== String(protId); });
      var optOut = ((cart && cart.attributes) || {})[optKey] === '1';
      return hasReal && !hasProt && !optOut;
    }
    // resolves once get() returns no running promise any more (also a new one that the previous one started), max
    // `rounds` waits. Undo uses it: removing the last product makes the protection line an orphan that a repair is
    // deleting right then; deciding "protection along?" before that repair finished lost the default-on insurance.
    function idle(get, rounds) {
      var p = get();
      if (!p || !(rounds > 0)) return Promise.resolve();
      function next() { return idle(get, rounds - 1); }
      return Promise.resolve(p).then(next, next);
    }
    // F4 undo toast position -> its CSS `bottom` in px, or null = the CSS default (16 px above the bottom edge: the empty
    // drawer, no footer). m = numbers measured in the viewport: top / bottom of the panel, footer = top of the footer,
    // car = {top, bottom} of the upsell carousel (null = none), head = bottom of the header, timer = bottom of the
    // countdown bar (0 = none), h = height of the toast, gap (8). The toast sits right above the block that is fixed at
    // the bottom: the footer, plus the carousel when that sits directly on the footer (normal layout; in a short
    // viewport, max-height 600px, the carousel scrolls with the content and is not counted). Round 4 (812x375): adding
    // the scrolling carousel put the toast above the screen, over the title. Clamped: its top never above the header /
    // countdown bar (+ gap), and never below the panel's bottom edge.
    function undoBottom(m) {
      if (!m || m.footer == null) return null;
      var gap = m.gap == null ? 8 : m.gap;
      var anchor = m.footer;
      if (m.car && m.car.bottom > m.car.top && Math.abs(m.car.bottom - m.footer) <= 2) anchor = m.car.top;
      var bottom = m.bottom - anchor + gap;
      var topLimit = Math.max(m.head == null ? m.top : m.head, m.timer || 0) + gap;
      var most = m.bottom - topLimit - (m.h || 0);   // the highest the toast may go
      if (bottom > most) bottom = most;
      return Math.max(0, Math.round(bottom));
    }
    return { totals: totals, snapshot: snapshot, undoable: undoable, needsProt: needsProt, idle: idle, undoBottom: undoBottom };
  })();
  /* tlsc-cart-core:end */

  /* tlsc-bundle-core:start
   * Bundle button (26 Oct 2026): "Neem er 2 voor 49,95" / "+1 = 4 voor 69,95" on a line of a product with a Kaching
   * quantity-break deal. Kaching's discount only looks at the TOTAL quantity of one product in the cart (all lines and
   * colours together), so the next tier is reached by raising one line. Price for N (verified on the cart, 26 Oct):
   * highest tier <= N with percentage p, N x variant price x (1 - p/100), then the whole euros kept and the cents set to
   * ,95 (59,126 -> 59,95; 63,9996 -> 63,95; p = 0: no rounding).
   * entry (from snippets/tls-cart-bundles.liquid): {c: currency, n: discount name, u: unit price in cents,
   * b: [[quantity, percent], ...], p: [price for 1, 2, ... up to the highest tier]}. Pure, unit-tested. */
  var BundleCore = (function () {
    // Kaching's ',95' rounding in exact integers: num = unit x q x (100000 - p x 1000) is the price in 1/100000 cent;
    // the whole euros of it are kept and the cents become 95
    function priceFor(unit, bars, q) {
      var p = 0;
      for (var i = 0; i < bars.length; i++) if (bars[i][0] <= q) p = bars[i][1];
      if (!p) return unit * q;
      var num = unit * q * (100000 - Math.round(p * 1000));
      return Math.floor(num / 10000000) * 100 + 95;
    }
    // a usable list entry: sane numbers, tiers ascending from >= 1, prices exactly what the rule gives
    function valid(entry) {
      if (!entry || typeof entry !== 'object') return false;
      var b = entry.b, p = entry.p, u = entry.u;
      if (!(u > 0) || Math.floor(u) !== u || !Array.isArray(b) || b.length < 2 || !Array.isArray(p)) return false;
      for (var i = 0; i < b.length; i++) {
        if (!Array.isArray(b[i]) || !(b[i][0] >= 1) || Math.floor(b[i][0]) !== b[i][0] || !(b[i][1] >= 0 && b[i][1] < 100)) return false;
        if (i && b[i][0] <= b[i - 1][0]) return false;
      }
      if (p.length !== b[b.length - 1][0]) return false;
      for (var q = 1; q <= p.length; q++) if (p[q - 1] !== priceFor(u, b, q)) return false;
      return true;
    }
    function linesOf(cart, pid) {
      return ((cart && cart.items) || []).filter(function (i) { return String(i.product_id) === String(pid); });
    }
    // the offer for product pid in this cart, or null. Shown only when the cart charges exactly what the list says for
    // the CURRENT quantity (so a changed deal / price / another discount on these lines means no button at all).
    function offer(cart, entry, pid) {
      if (!valid(entry)) return null;
      var lines = linesOf(cart, pid);
      if (!lines.length) return null;
      if (entry.c && cart.currency && String(cart.currency) !== String(entry.c)) return null;
      var q = 0, paid = 0;
      for (var i = 0; i < lines.length; i++) {
        var l = lines[i];
        if (Number(l.original_price) !== entry.u || l.selling_plan_allocation) return null;
        var al = l.line_level_discount_allocations || [];
        for (var j = 0; j < al.length; j++) {
          var title = al[j] && al[j].discount_application && al[j].discount_application.title;
          if (!entry.n || title !== entry.n) return null;
        }
        q += Number(l.quantity) || 0;
        paid += Number(l.final_line_price) || 0;
      }
      if (q < 1 || q >= entry.p.length) return null;   // highest tier reached (or beyond): nothing to offer
      if (paid !== entry.p[q - 1]) return null;
      var to = 0;
      for (var k = 0; k < entry.b.length; k++) if (entry.b[k][0] > q) { to = entry.b[k][0]; break; }
      if (!to) return null;
      return { pid: String(pid), key: lines[0].key, from: q, to: to, add: to - q,
        lineQty: Number(lines[0].quantity) + (to - q), price: entry.p[to - 1] };
    }
    function text(o, t1, t2, money) {
      var tpl = o.from === 1 ? t1 : t2;
      return String(tpl || '').split('[n]').join(String(o.to)).split('[k]').join(String(o.add)).split('[price]').join(money);
    }
    // stock (review 27 Oct): `stock` = how many of the line's variant the shop sells at most; the section passes it only
    // for a variant that is tracked AND stops selling at 0, capped at 10 since round 4 (still exact: have + add never
    // exceeds the product's highest tier, max 8 in the list) (null / '' = no limit, e.g. "continue selling"). The click
    // raises line o.key by o.add, so all lines of that variant together must still fit - otherwise no button (the tap
    // would only end in an error message).
    function fits(cart, o, stock) {
      if (stock == null || stock === '') return true;
      var max = Number(stock);
      var items = (cart && cart.items) || [];
      var line = items.filter(function (i) { return i.key === o.key; })[0];
      if (!line || !isFinite(max)) return false;
      var have = 0;
      items.forEach(function (i) { if (String(i.variant_id) === String(line.variant_id)) have += Number(i.quantity) || 0; });
      return have + o.add <= max;
    }
    // after the click: the cart must now hold o.to items of the product and charge exactly o.price for them
    function verify(cart, o) {
      var lines = linesOf(cart, o.pid);
      var q = 0, paid = 0;
      lines.forEach(function (l) { q += Number(l.quantity) || 0; paid += Number(l.final_line_price) || 0; });
      return { ok: q === o.to && paid === o.price, qty: q, paid: paid };
    }
    // the deal tiers Kaching itself prints on the product page ([[q, p], ...]) or null when it is not a plain
    // quantity-break / percentage deal; equal() compares them with the list (a changed deal = no button until re-run)
    function pageBars(deal) {
      var out = [];
      var bars = (deal && deal.dealBars) || [];
      for (var i = 0; i < bars.length; i++) {
        var x = bars[i];
        if (!x || x.dealBarType !== 'quantity-break') return null;
        if (x.discountType === 'default') out.push([x.quantity, 0]);
        else if (x.discountType === 'percentage') out.push([x.quantity, Number(x.discountValue)]);
        else return null;
      }
      out.sort(function (a, b) { return a[0] - b[0]; });
      return out.length ? out : null;
    }
    function equal(a, b) {
      return !!(a && b) && a.length === b.length && a.every(function (x, i) { return x[0] === b[i][0] && x[1] === b[i][1]; });
    }
    return { priceFor: priceFor, valid: valid, offer: offer, text: text, fits: fits, verify: verify, pageBars: pageBars, equal: equal };
  })();
  /* tlsc-bundle-core:end */

  // ---------- network ----------
  // every request of the drawer: time limit LIMIT, a usable JSON answer or an Error with .kind (see NetCore)
  function getJSON(url, opts) {
    return NetCore.request(nativeFetch, url, opts, LIMIT).then(function (res) {
      if (res.ok && res.json && res.data) return res.data;
      throw NetCore.failure(res);
    });
  }
  function postJSON(url, body) {
    return getJSON(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }
  // quiet: a background check (drawer just opened from the icon) - leave the drawer alone when nothing changed
  function fetchSection(quiet) {
    var at = state.mut;
    return getJSON(ROOT + '?sections=' + SECTION, { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (j) {
        // a cart change sent after this read brings its own, newer render: this one is outdated
        if (quiet && at !== state.mut) return;
        if (j && j[SECTION]) swap(j[SECTION], quiet);
      });
  }

  // ---------- rendering ----------
  function readCart() {
    var s = $('[data-tlsc-cart]');
    if (!s) return null;
    try { return JSON.parse(s.textContent); } catch (e) { return null; }
  }
  // what the customer sees; the cart JSON is left out (Shopify gives its discount data a new key/timestamp on every render)
  function visibleMarkup(node) {
    var c = node.cloneNode(true);
    var s = c.querySelector('[data-tlsc-cart]');
    if (s) s.textContent = '';
    return c.innerHTML;
  }
  function swap(html, quiet) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-tlsc-live]');
    if (!fresh) return;
    var seen = visibleMarkup(fresh);
    if (quiet && seen === state.lastMarkup) {
      // nothing visible changed: only take over the fresh cart data, no redraw
      var cur = $('[data-tlsc-cart]');
      var nu = fresh.querySelector('[data-tlsc-cart]');
      if (cur && nu) cur.textContent = nu.textContent;
      state.cart = readCart() || state.cart;
      state.stale = false;
      timerUpdate();
      bundleRender();
      fixProtection();
      return;
    }
    state.lastMarkup = seen;
    var live = $('[data-tlsc-live]');
    var oldUpsells = $('[data-tlsc-upsells]');
    live.innerHTML = fresh.innerHTML;
    // keep the already built carousel in place while the new one loads (no flicker)
    var mount = $('[data-tlsc-upsells]');
    if (mount && oldUpsells && oldUpsells.childNodes.length) mount.parentNode.replaceChild(oldUpsells, mount);
    state.cart = readCart();
    state.stale = false;
    timerUpdate();   // same task as the redraw: the countdown bar is back before the browser paints
    bundleRender();  // same for the bundle buttons
    if (undo.snap) { var ub = $('[data-tlsc-undo]'); if (ub) placeUndo(ub); }   // the footer height may have changed
    checkTotals();
    if (state.cart) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: state.cart }, bubbles: true }));
      if (fixProtection()) return;
    }
    renderRecommendations();
  }

  // ---------- shipping protection: cart rules, checked after every render (also when nothing visible changed) ----------
  // never on its own, never more than one (also not as two separate lines: the auto and the switch add carry a
  // different _bron), and never auto-added against the customer's opt-out. Returns {line key: new qty} or null.
  function protectionFixes(cart) {
    var items = (cart && cart.items) || [];
    if (!state.protId || !items.length) return null;
    var prots = items.filter(function (i) { return String(i.product_id) === state.protId; });
    if (!prots.length) return null;
    var upd = {};
    var n = 0;
    if (prots.length === items.length) {
      prots.forEach(function (p) { upd[p.key] = 0; n++; });
      return upd;
    }
    var optOut = ((cart.attributes) || {})[OPTOUT] === '1';
    var kept = false;
    prots.forEach(function (p) {
      var auto = ((p.properties || {})._bron || '') === 'cart-addon:protection-auto';
      var want = !kept && !(optOut && auto) ? 1 : 0;
      if (want) kept = true;
      if (p.quantity !== want) { upd[p.key] = want; n++; }
    });
    return n ? upd : null;
  }
  // one repair at a time (cannot loop); a failed repair is tried once more on the next render
  function fixProtection() {
    var upd = protectionFixes(state.cart);
    if (!upd || state.protFixing) return false;
    if (state.protTries >= 2) return false;
    state.protTries = (state.protTries || 0) + 1;
    state.protFixing = true;
    function done() {
      state.protFixing = false;
      state.fixing = null;
      if (!protectionFixes(state.cart)) state.protTries = 0;
      // a real product arrived while this repair deleted an orphan protection line (undo, add-to-cart at that moment):
      // default-on again (never against the opt-out: needsProtection checks it)
      if (!state.stale && needsProtection(state.cart, false)) return addProtection();
    }
    state.fixing = updateCart({ updates: upd }).then(done, done);
    return true;
  }

  // ---------- shipping protection, default on ----------
  // Added in the SAME request that renders the drawer (add.js with sections), so the drawer is drawn once.
  // assumeRealItem: right after an add-to-cart the (possibly stale) cart state may not show the new product yet.
  function needsProtection(cart, assumeRealItem) {
    if (!state.protId || !state.protDefault || !state.protVariant || state.autoAddFailed) return false;
    return CartCore.needsProt(cart, state.protId, assumeRealItem, OPTOUT);
  }
  function addProtection() {
    if (!state.protPending) {
      state.protPending = addVariant(state.protVariant, 'cart-addon:protection-auto', true).then(function (ok) {
        state.protPending = null;
        if (!ok) state.autoAddFailed = true;   // never loop on a failing add
        return ok;
      });
    }
    return state.protPending;
  }
  function protectionItem() {
    return { id: Number(state.protVariant), quantity: 1, properties: { _bron: 'cart-addon:protection-auto' } };
  }
  // one request that brings the drawer up to date (incl. protection when needed). Only decided on state.cart when
  // that is known to be current; after a tab switch / back button it is read fresh first (opt-out made elsewhere).
  function syncCart(assumeRealItem, quiet) {
    if (!state.stale && needsProtection(state.cart, assumeRealItem)) {
      return addProtection().then(function (ok) { if (!ok) return fetchSection(quiet); });
    }
    return fetchSection(quiet).then(function () {
      // stale still true = this read was dropped for a newer cart change: that change brings its own render
      if (!state.stale && needsProtection(state.cart, false)) return addProtection();
    });
  }
  function setBusy(on) {
    state.busyCount += on ? 1 : -1;
    if (state.busyCount < 0) state.busyCount = 0;
    el.classList.toggle('is-busy', state.busyCount > 0);
  }
  // F2: one short translated message (Error from NetCore, or a text); the box is a live region (role=alert) that stays
  // in the page, empty = invisible, so screen readers read every new message
  function showError(err) {
    var box = $('[data-tlsc-error]');
    if (!box) return;
    var msg = typeof err === 'string' ? err : NetCore.message(err, T);
    hideUndo();
    box.textContent = '';
    clearTimeout(showError.t);
    clearTimeout(showError.s);
    showError.s = setTimeout(function () { box.textContent = msg; }, 30);
    showError.t = setTimeout(function () { box.textContent = ''; }, 6000);
  }

  // ---------- F2: the checkout button waits for a cart change that is still on its way ----------
  // the failure baseline of a checkout tap = the failure count when the oldest change still on its way started
  var pending = NetCore.Pending({ fails: function () { return state.fails; } });
  function track(p) { return pending.add(p); }
  // a cart change the customer made (or was told about) failed: counted, so a checkout tap that waits for it stays; and
  // the switch + the quantity field of the failed line show what the cart really holds again AT ONCE, not only after the
  // re-read (review 27 Oct). sent = {line key: quantity sent} of the failed change (none for an add).
  function failed(sent) { state.fails++; state.failAt = Date.now(); syncShown(sent); }
  var FRESH_FAIL = 2000;   // ms: a failure younger than this holds one checkout tap, whatever else is on its way
  // e.g. the customer switched the shipping protection off and taps checkout right away: without waiting, the checkout
  // could open before that change arrived. Wait for it (max 3 s), then go on to the checkout - unless a change of this
  // busy stretch failed (also just BEFORE the tap): then stay, with the error and the re-read drawer (the switch shows
  // what the cart really holds) visible. A failure of the last 2 s that no tap was held for yet holds the tap once
  // ALWAYS: also when its re-read is already done, and also while a newer change (e.g. a '+' tapped after the failure,
  // round 4) is still on its way. The next tap goes on. Closing the drawer while it waits cancels the checkout.
  function checkoutClick(e, link) {
    var fresh = NetCore.fresh(Date.now(), state.failAt || 0, state.fails, state.failShown || 0, FRESH_FAIL);
    if (!pending.count() && !fresh) return;   // nothing on its way, no failure the customer may have missed: the normal link
    e.preventDefault();
    if (state.leaving) return;
    // stay: the message + real switch first. Rebased, so the next tap only waits for (and stays for) NEW failures.
    if (fresh) { state.failShown = state.fails; pending.rebase(); return; }
    var id = state.leaving = (state.leaveSeq = (state.leaveSeq || 0) + 1);
    var href = link.getAttribute('href') || '/checkout';
    link.setAttribute('aria-busy', 'true');
    el.classList.add('is-leaving');
    NetCore.leave(pending, function () { return state.fails; }, 3000).then(function (go) {
      if (state.leaving !== id) return;   // cancelled meanwhile: drawer closed (or page restored from the back button)
      if (go) { window.location.href = href; return; }
      state.failShown = state.fails;
      leaveDone(link);
    });
  }
  // the checkout button works normally again (stay after a failure, drawer closed while waiting, back from checkout)
  function leaveDone(link) {
    state.leaving = 0;
    if (!el) return;
    el.classList.remove('is-leaving');
    if (link) link.removeAttribute('aria-busy');
    var co = $('[data-tlsc-checkout]');
    if (co) co.removeAttribute('aria-busy');
  }
  // the protection switch shows what the cart really holds (after a failed switch change the re-read can fail as well)
  function protSwitchSync() {
    var sw = $('[data-tlsc-protection]');
    if (sw) sw.checked = ((state.cart && state.cart.items) || []).some(function (i) { return String(i.product_id) === state.protId; });
  }
  // after a failed change: the protection switch and the quantity field of the line(s) whose change failed show the last
  // known cart again right away (the change did not go through; the quiet re-read that follows corrects anything else).
  // Round 4: every field was reset, also a quantity the customer was typing on ANOTHER line. Now only the failed line(s)
  // (sent = {key: quantity sent}); and a focused field there is left alone when it no longer holds what was sent (the
  // customer is already typing a new number in it).
  function syncShown(sent) {
    if (!el || !state.cart) return;
    protSwitchSync();
    if (!sent) return;
    var items = state.cart.items || [];
    Array.prototype.forEach.call(el.querySelectorAll('[data-tlsc-live] .tlsc-item[data-key]'), function (row) {
      var key = row.getAttribute('data-key');
      if (!Object.prototype.hasOwnProperty.call(sent, key)) return;
      var inp = row.querySelector('[data-tlsc-qty-input]');
      var it = items.filter(function (i) { return i.key === key; })[0];
      if (!inp || !it || String(inp.value) === String(it.quantity)) return;
      if (inp === document.activeElement && String(inp.value) !== String(sent[key])) return;
      inp.value = it.quantity;
    });
  }

  // ---------- F4: undo after removing a line ----------
  var UNDO_MS = 8000;
  var undo = { snap: null, t: null };
  function focusEl(n) {
    if (!n || !n.focus) return;
    try { n.focus({ preventScroll: true }); } catch (e) { n.focus(); }
  }
  // where the focus goes when the undo toast disappears while it has the focus: the empty drawer's "Verder winkelen",
  // else the first product line, else the close button
  function restingFocus() {
    return $('[data-tlsc-live] [data-tlsc-continue]') || $('[data-tlsc-live] .tlsc-item__title') || $('.tlsc__close');
  }
  // fix round 3 (27 Oct): the toast sits right above the fixed bottom block (upsell carousel + footer, so the carousel's
  // add button and the checkout stay free), or near the bottom edge in the empty drawer - never over the countdown bar
  // or the "cart is empty" heading at the top (CSS: bottom-anchored). Round 4: measured (CartCore.undoBottom), so a
  // short viewport where the carousel scrolls with the content keeps it on screen, below the header + countdown.
  function placeUndo(box) {
    var panel = $('.tlsc__panel');
    var ft = $('[data-tlsc-live] .tlsc__footer');
    if (!panel || !ft || !ft.offsetHeight) { box.style.bottom = ''; return; }
    var up = $('[data-tlsc-live] [data-tlsc-upsells]');
    var head = $('[data-tlsc-live] .tlsc__header');
    var bar = $('[data-tlsc-live] [data-tlsc-timer]');
    var p = panel.getBoundingClientRect();
    var u = up && up.offsetHeight ? up.getBoundingClientRect() : null;
    var b = CartCore.undoBottom({ top: p.top, bottom: p.bottom, footer: ft.getBoundingClientRect().top,
      car: u ? { top: u.top, bottom: u.bottom } : null, head: head ? head.getBoundingClientRect().bottom : null,
      timer: bar && bar.offsetHeight ? bar.getBoundingClientRect().bottom : 0, h: box.offsetHeight, gap: 8 });
    box.style.bottom = b == null ? '' : b + 'px';
  }
  // keep: the caller moves the focus itself (undo clicked: the focus goes to the line that comes back)
  function hideUndo(keep) {
    clearTimeout(undo.t);
    undo.snap = null;
    var box = $('[data-tlsc-undo]');
    if (!box) return;
    var had = !!document.activeElement && box.contains(document.activeElement);
    box.innerHTML = '';
    box.style.bottom = '';
    if (had && !keep && state.open) focusEl(restingFocus());
  }
  function showUndo(snap) {
    var box = $('[data-tlsc-undo]');
    if (!box || !snap) return;
    clearTimeout(undo.t);
    undo.snap = snap;
    box.innerHTML = '<span class="tlsc-undo__text">' + esc(T.removed) + '</span><span aria-hidden="true"> \u00b7 </span>' +
      '<button type="button" class="tlsc-undo__btn" data-tlsc-undo-btn>' + esc(T.undo) + '</button>';
    placeUndo(box);
    undo.t = setTimeout(function () { hideUndo(); }, UNDO_MS);
    // the trash button that had the focus is gone with the redraw: keyboard / screen reader users land on "Terugzetten"
    if (state.open) focusEl(box.querySelector('[data-tlsc-undo-btn]'));
  }
  function undoRemove() {
    var snap = undo.snap;
    var box = $('[data-tlsc-undo]');
    var kb = !!(box && document.activeElement && box.contains(document.activeElement));
    hideUndo(true);
    if (!CartCore.undoable(snap, Date.now(), UNDO_MS + 1000)) { if (kb) focusEl(restingFocus()); return; }
    if (kb) focusEl(restingFocus());   // for the moment; the redraw below replaces it
    // first let a running protection repair finish (removing the last product made the protection line an orphan that
    // is being deleted right now), so "protection along?" is decided on the cart as it is then. Tracked as a whole:
    // a checkout tap meanwhile waits for the product to be back.
    track(CartCore.idle(function () { return state.fixing; }, 3).then(function () { return addItem(snap.item); })
      .then(function (ok) {
        // the focus was on "Terugzetten": now on the line that came back (or the resting place when it did not)
        if (!kb || !state.open || (document.activeElement && document.activeElement !== document.body && el.contains(document.activeElement))) return ok;
        var back = Array.prototype.filter.call(el.querySelectorAll('[data-tlsc-live] .tlsc-item[data-key]'), function (r) {
          return String(r.getAttribute('data-key')).split(':')[0] === String(snap.item.id);
        })[0];
        focusEl((back && back.querySelector('.tlsc-item__title')) || restingFocus());
        return ok;
      }));
  }

  // ---------- bundle button ("neem er nog 1" to the next Kaching tier) ----------
  var bundle = { on: false, data: {}, off: {} };
  var BUNDLE_OFF = 'tlsc-bundle-off';
  function bundleInit() {
    bundle.on = el.getAttribute('data-bundle') === 'on';
    if (!bundle.on) return;
    try {
      var s = el.querySelector('[data-tlsc-bundles]');
      bundle.data = (s && JSON.parse(s.textContent).products) || {};
    } catch (e) { bundle.data = {}; }
    try { (JSON.parse(sessionStorage.getItem(BUNDLE_OFF) || '[]') || []).forEach(function (pid) { bundle.off[pid] = true; }); } catch (e) { /* no storage */ }
    // on a product page Kaching prints its own deal: when its tiers are not the ones in the list, the list is outdated
    // for that product (deal changed in Kaching) -> no button for it until the list is made again
    try {
      Array.prototype.forEach.call(document.querySelectorAll('script.kaching-bundles-deal-block-settings[data-product-id]'), function (s) {
        var pid = s.getAttribute('data-product-id');
        var entry = bundle.data[pid];
        if (!entry || bundle.off[pid]) return;
        var bars = null;
        try { bars = BundleCore.pageBars(JSON.parse(s.textContent)); } catch (e) { bars = null; }
        if (!BundleCore.equal(bars, entry.b)) bundleOff(pid, 'deal on the page differs from the list', bars);
      });
    } catch (e) { /* keep the list */ }
  }
  function bundleOff(pid, why, info) {
    bundle.off[pid] = true;
    try { sessionStorage.setItem(BUNDLE_OFF, JSON.stringify(Object.keys(bundle.off))); } catch (e) { /* no storage */ }
    try { console.warn('[tls-cart] bundle button off for product ' + pid + ': ' + why, info || ''); } catch (e) { /* ignore */ }
  }
  function bundleRender() {
    if (!bundle.on) return;
    var live = $('[data-tlsc-live]');
    if (!live) return;
    var slots = live.querySelectorAll('[data-tlsc-bundle-slot]');
    var offers = {};
    Array.prototype.forEach.call(slots, function (slot) {
      var pid = slot.getAttribute('data-pid');
      if (!(pid in offers)) offers[pid] = bundle.off[pid] ? null : BundleCore.offer(state.cart, bundle.data[pid], pid);
      var o = offers[pid];
      var item = slot.closest('[data-key]');
      var html = '';
      // data-stock: only on a line whose variant stops selling at 0 (section); not enough of it = no button
      if (o && item && item.getAttribute('data-key') === o.key && BundleCore.fits(state.cart, o, slot.getAttribute('data-stock'))) {
        // 30 Oct: the amount never breaks over two lines ("79,95" / "€" in the narrow -/+ column)
        var label = BundleCore.text(o, T.bundle1, T.bundle2, formatMoney(o.price).replace(/ /g, '\u00a0'));
        html = '<button type="button" class="tlsc-bundle" data-tlsc-bundle="' + o.lineQty + '" data-to="' + o.to +
          '" data-price="' + o.price + '" data-pid="' + esc(o.pid) + '">' +
          '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" focusable="false"><path d="M10 4v12M4 10h12" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>' +
          '<span>' + esc(label) + '</span></button>';
      }
      if (slot.innerHTML !== html) slot.innerHTML = html;
    });
  }
  function bundleClick(btn) {
    var item = btn.closest('[data-key]');
    if (!item) return;
    var o = { pid: btn.getAttribute('data-pid'), to: parseInt(btn.getAttribute('data-to'), 10),
      price: parseInt(btn.getAttribute('data-price'), 10) };
    changeLine(item.getAttribute('data-key'), parseInt(btn.getAttribute('data-tlsc-bundle'), 10)).then(function (ok) {
      if (!ok || !state.cart) return;
      // the promise on the button must be exactly what the cart now charges; if not, never show it again this visit
      var v = BundleCore.verify(state.cart, o);
      if (!v.ok) { bundleOff(o.pid, 'cart charges ' + v.paid + ' for ' + v.qty + ', button said ' + o.price + ' for ' + o.to); bundleRender(); }
    });
  }
  // F1 guard (debug aid only): the totals the section prints must add up; never changes anything
  function checkTotals() {
    var t = state.cart && CartCore.totals(state.cart);
    if (t && !t.ok) { try { console.warn('[tls-cart] totals do not add up', t); } catch (e) { /* ignore */ } }
  }

  // ---------- recommendations (upsells + empty state) ----------
  function recsFor(pid) {
    if (!state.recCache[pid]) {
      state.recCache[pid] = getJSON(ROOT + 'recommendations/products.json?product_id=' + pid + '&limit=10&intent=related', {})
        .then(function (j) { return (j && j.products) || []; })
        .catch(function () { return []; });
    }
    return state.recCache[pid];
  }
  function availableVariants(p) {
    return (p.variants || []).filter(function (v) { return v.available; });
  }
  function pricesHTML(v) {
    var out = '';
    if (v.compare_at_price && v.compare_at_price > v.price) out += '<s>' + esc(formatMoney(v.compare_at_price)) + '</s>';
    return out + '<span>' + esc(formatMoney(v.price)) + '</span>';
  }
  // the tile's starting variant: the first available one, except a product with a plug option on a store that loads
  // tls-plug.js (.com only, 20 Oct): the visitor's plug in the same colour (window.tlsPlug.preferredVariant).
  // NL/DE have no tlsPlug: unchanged.
  function startVariant(p, vs) {
    var v = vs[0];
    try {
      var pv = window.tlsPlug && window.tlsPlug.preferredVariant ? window.tlsPlug.preferredVariant(p, v) : null;
      if (pv && vs.indexOf(pv) >= 0) v = pv;
    } catch (e) { /* keep the first available variant */ }
    return v;
  }
  function tileHTML(p) {
    var vs = availableVariants(p);
    var v = startVariant(p, vs);
    state.products[p.id] = p;
    var url = p.url || ('/products/' + p.handle);
    var img = imgUrl((v.featured_image && v.featured_image.src) || p.featured_image, 200);
    var select = '';
    if (p.variants.length > 1) {
      select = '<select class="tlsc-up__select" data-tlsc-variant aria-label="' + esc(p.title) + '">' +
        vs.map(function (x) { return '<option value="' + x.id + '"' + (x === v ? ' selected' : '') + '>' + esc(x.title) + '</option>'; }).join('') +
        '</select>';
    }
    return '<div class="tlsc-up" data-tlsc-product="' + p.id + '" data-tlsc-src="' + esc(p._src || 'related') + '">' +
      '<a class="tlsc-up__img" href="' + esc(url) + '"><img src="' + esc(img) + '" alt="' + esc(p.title) + '" loading="lazy" width="80" height="80"></a>' +
      '<div class="tlsc-up__main">' +
        '<a class="tlsc-up__title" href="' + esc(url) + '">' + esc(p.title) + '</a>' +
        '<div class="tlsc-up__prices" data-tlsc-prices>' + pricesHTML(v) + '</div>' +
        '<div class="tlsc-up__row">' + select +
          '<button type="button" class="tlsc-up__add" data-tlsc-add="' + v.id + '">' + esc(T.add) + '</button>' +
        '</div>' +
      '</div>' +
    '</div>';
  }
  var ARROW_L = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ARROW_R = '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function buildCarousel(mount, products) {
    if (!products.length) { mount.innerHTML = ''; return; }
    var many = products.length > 1;
    mount.innerHTML = '<div class="tlsc-car">' +
      (many ? '<button type="button" class="tlsc-car__arrow tlsc-car__arrow--prev" data-tlsc-car="-1" aria-label="' + esc(T.prev) + '">' + ARROW_L + '</button>' : '') +
      '<div class="tlsc-car__track" data-tlsc-track>' + products.map(tileHTML).join('') + '</div>' +
      (many ? '<button type="button" class="tlsc-car__arrow tlsc-car__arrow--next" data-tlsc-car="1" aria-label="' + esc(T.next) + '">' + ARROW_R + '</button>' : '') +
      '</div>';
  }
  function buildList(mount, products) {
    mount.innerHTML = products.map(tileHTML).join('');
  }
  function readJSON(sel) {
    var s = $(sel);
    if (!s) return [];
    try { return JSON.parse(s.textContent) || []; } catch (e) { return []; }
  }
  function fallbackProducts() { return readJSON('[data-tlsc-fallback]'); }
  function carouselIds(mount) {
    return Array.prototype.map.call(mount.querySelectorAll('[data-tlsc-product]'), function (t) {
      return t.getAttribute('data-tlsc-product');
    });
  }
  // Show `list` in the carousel. If the carousel already starts with the same products, only append the rest,
  // so a customer who is already swiping is never thrown back to the first tile.
  function showCarousel(mount, list) {
    var have = carouselIds(mount);
    var want = list.map(function (p) { return String(p.id); });
    var samePrefix = have.length > 1 && have.every(function (id, i) { return want[i] === id; });
    if (samePrefix && want.length >= have.length) {
      var track = mount.querySelector('[data-tlsc-track]');
      list.slice(have.length).forEach(function (p) { track.insertAdjacentHTML('beforeend', tileHTML(p)); });
      return;
    }
    if (have.join() !== want.join()) buildCarousel(mount, list);
  }
  function renderRecommendations() {
    var cart = state.cart;
    var mount = $('[data-tlsc-upsells]');
    var emptyMount = $('[data-tlsc-empty-recs]');
    if (cart && cart.item_count > 0 && mount) {
      var inCart = {};
      var pids = [];
      if (state.protId) inCart[state.protId] = true;
      cart.items.forEach(function (i) {
        inCart[i.product_id] = true;
        if (pids.indexOf(i.product_id) < 0 && String(i.product_id) !== state.protId) pids.push(i.product_id);
      });
      // drop tiles of products that are now in the cart right away (e.g. the upsell that was just added)
      Array.prototype.forEach.call(mount.querySelectorAll('[data-tlsc-product]'), function (t) {
        if (inCart[t.getAttribute('data-tlsc-product')]) t.parentNode.removeChild(t);
      });
      var seen = {};
      var out = [];
      function take(p) {
        if (out.length >= 10 || seen[p.id] || inCart[p.id] || !availableVariants(p).length) return;
        seen[p.id] = true;
        out.push(p);
      }
      // 1) "vaak samen gekocht" from past orders: already in the section HTML, so it shows instantly
      readJSON('[data-tlsc-fbt]').forEach(take);
      if (out.length) showCarousel(mount, out.slice());
      // 2) top up with Shopify's related recommendations (fetched once per product, then cached)
      var token = (renderRecommendations.token = (renderRecommendations.token || 0) + 1);
      Promise.all(pids.slice(0, 4).map(recsFor)).then(function (lists) {
        if (token !== renderRecommendations.token) return;
        lists.forEach(function (list) { list.forEach(take); });
        var target = $('[data-tlsc-upsells]');
        if (!target) return;
        if (out.length) showCarousel(target, out);
        else target.innerHTML = '';
      });
    } else if (emptyMount) {
      var pid = pageProductId();
      var source = pid ? recsFor(pid) : Promise.resolve(fallbackProducts());
      source.then(function (list) {
        if (!list.length && pid) list = fallbackProducts();
        var target = $('[data-tlsc-empty-recs]');
        if (target) buildList(target, list.filter(function (p) {
          return availableVariants(p).length && String(p.id) !== state.protId;
        }).slice(0, 3));
      });
    }
  }

  // ---------- cart actions ----------
  // Every cart change: tracked (checkout waits for it), time-limited, a friendly message on failure, and the drawer is
  // read fresh after a failure. Resolves true when the change went through, false when not (never rejects).
  function changeLine(key, qty) {
    setBusy(true);
    state.mut++;
    var ok = true;
    qty = Math.max(0, qty || 0);
    // F4 undo: what the line was, so "Terugzetten" can put it back exactly (removing = quantity 0, also via - or typing 0)
    var line = qty === 0 ? ((state.cart && state.cart.items) || []).filter(function (i) { return i.key === key; })[0] : null;
    var snap = line && String(line.product_id) !== state.protId ? CartCore.snapshot(line, Date.now()) : null;
    if (qty > 0) hideUndo();
    var sent = {};
    sent[key] = qty;
    return track(postJSON(ROOT + 'cart/change.js', { id: key, quantity: qty, sections: SECTION })
      .then(function (j) {
        if (j.sections && j.sections[SECTION]) swap(j.sections[SECTION]);
        else return fetchSection();
      })
      // after a failure the re-read is quiet: no redraw when nothing changed (a quantity being typed on another line stays)
      .catch(function (e) { ok = false; failed(sent); showError(e); return fetchSection(true).catch(function () { /* offline */ }); })
      .then(function () { setBusy(false); signal(); if (ok && snap) showUndo(snap); return ok; }));
  }
  function addVariant(id, source, silent) {
    return addItem({ id: Number(id), quantity: 1, properties: { _bron: source || 'cart-upsell:related' } }, silent);
  }
  function addItem(item, silent) {
    setBusy(true);
    state.mut++;
    var ok = true;
    var items = [item];
    // a product added from the drawer itself (e.g. the empty state, undo): protection goes along in the same request
    var real = String(item.id) !== state.protVariant;
    var stale = state.stale;
    var withProt = real && !stale && !state.protPending && needsProtection(state.cart, true);
    if (withProt) items.push(protectionItem());
    function send(list) { return postJSON(ROOT + 'cart/add.js', { items: list, sections: SECTION }); }
    return track(send(items)
      .catch(function (e) {
        // Shopify refused the add (e.g. protection sold out): the customer's product still goes in, on its own. ONLY
        // then: after a timeout / no answer / 429 / 5xx the first add may have been saved, and sending the product again
        // put it in twice (review 27 Oct); the drawer is read fresh below instead and shows what the cart really holds
        if (!withProt || !NetCore.refused(e)) throw e;
        return send(items.slice(0, 1)).then(function (j) { state.autoAddFailed = true; return j; });
      })
      .then(function (j) {
        if (j.sections && j.sections[SECTION]) swap(j.sections[SECTION]);
        else return fetchSection();
      })
      // checked again on the fresh render: the cart state was not current before the add, or a protection repair /
      // auto add was still running when withProt was decided (undo right after removing the last product)
      .then(function () { if (real && needsProtection(state.cart, false)) return addProtection(); })
      // any failure: the drawer (lines, protection switch) is read fresh, like changeLine / updateCart - an add that
      // timed out may still have gone through, and a second tap must see that
      .catch(function (e) {
        ok = false;
        if (!silent) { failed(); showError(e); }
        return fetchSection(true).catch(function () { /* offline */ });
      })
      .then(function () { setBusy(false); signal(); return ok; }));
  }
  function updateCart(body) {
    setBusy(true);
    state.mut++;
    body.sections = SECTION;
    var ok = true;
    return track(postJSON(ROOT + 'cart/update.js', body)
      .then(function (j) {
        if (j.sections && j.sections[SECTION]) swap(j.sections[SECTION]);
        else return fetchSection();
      })
      .catch(function (e) { ok = false; failed(body.updates); showError(e); return fetchSection(true).catch(function () { /* offline */ }); })
      .then(function () { setBusy(false); signal(); return ok; }));
  }
  // tell the shop's other open tabs that the cart changed (they read it fresh before their next decision)
  function signal() {
    try { localStorage.setItem('tlsc-cart-changed', String(Date.now())); } catch (e) { /* private mode */ }
  }
  /* tlsc-other-tab:start
   * Another tab changed the cart ('tlsc-cart-changed') or the countdown ('tlsc-timer'): this tab never decides on its
   * own copy until it has read the cart again. One drawer change there writes BOTH keys right after each other, so a
   * burst of these events is merged into ONE section request (sent 200 ms after the first event; a hidden tab reads it
   * fresh when it is shown again, a cart change of this tab itself in between makes the read unnecessary). */
  var otherTabRead = null;
  function otherTabChanged() {
    state.stale = true;
    if (document.hidden || otherTabRead) return;
    otherTabRead = setTimeout(function () {
      otherTabRead = null;
      if (state.stale && !document.hidden) fetchSection(true).catch(function () { /* next action reads it fresh */ });
    }, 200);
  }
  /* tlsc-other-tab:end */

  /* tlsc-timer-ui:start
   * "Cart reserved" countdown bar (venek 25 Oct 2026: NL + DE, November 2026; settings 'Afteltimer' in the section).
   * Reads only the cart the drawer already has (state.cart) and its own storage key: it never sends a cart request and
   * never touches the checkout. Drawn between the header and the rewards bar while the cart has real products; one
   * 1-second interval at most, only while the drawer is open, the tab is visible and the countdown runs. */
  var TIMER_KEY = 'tlsc-timer';
  var TIMER_TEST = 'tlsc-timer-test';
  var CLOCK = '<svg class="tlsc-timer__icon" viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="6.6" stroke="currentColor" stroke-width="1.5" fill="none"/>' +
    '<path d="M8 4.6V8l2.4 1.5" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var timer = { cfg: null, box: null, sr: null, test: false, leftMs: null, prevSig: null, prevView: null, say: null,
    last: null, al: null, iv: null, sayTimer: null };

  function memoryStore() {
    var m = {};
    return { getItem: function (k) { return Object.prototype.hasOwnProperty.call(m, k) ? m[k] : null; },
      setItem: function (k, v) { m[k] = String(v); }, removeItem: function (k) { delete m[k]; } };
  }
  function usableStore(name) {
    try {
      var s = window[name];
      s.setItem('tlsc-probe', '1');
      s.removeItem('tlsc-probe');
      return s;
    } catch (e) { return null; }
  }
  function timerRead() {
    try {
      var v = timer.box.getItem(TIMER_KEY);
      return v ? JSON.parse(v) : null;
    } catch (e) { return null; }
  }
  function timerWrite(st) {
    try {
      if (st) timer.box.setItem(TIMER_KEY, JSON.stringify(st));
      else timer.box.removeItem(TIMER_KEY);
    } catch (e) { /* storage full / blocked: the bar still works on this page */ }
  }
  function timerInit() {
    if (el.getAttribute('data-timer') !== 'on') return;   // switched off, or not the NL/DE shop: nothing at all
    timer.cfg = {
      dur: Math.max(1, parseInt(el.getAttribute('data-timer-minutes'), 10) || 10) * 60000,
      start: el.getAttribute('data-timer-start') || '',
      end: el.getAttribute('data-timer-end') || '',
      text: el.getAttribute('data-timer-text') || '',
      expired: el.getAttribute('data-timer-expired') || '',
      unit: el.getAttribute('data-timer-unit') || 'min',
      unit1: el.getAttribute('data-timer-unit1') || 'min'
    };
    timer.box = usableStore('localStorage') || usableStore('sessionStorage') || memoryStore();
    var session = usableStore('sessionStorage');
    var flag = null;
    try { flag = session ? session.getItem(TIMER_TEST) : null; } catch (e) { /* ignore */ }
    var p = TimerCore.testParams(window.location.search, flag);
    try {
      if (session && p.flag === '1') session.setItem(TIMER_TEST, '1');
      if (session && p.flag === '0') session.removeItem(TIMER_TEST);
    } catch (e) { /* ignore */ }
    timer.test = p.test;
    timer.leftMs = p.leftMs;
    // polite announcements (start + expiry only), outside the redrawn part so it survives every redraw
    var panel = $('.tlsc__panel');
    if (panel) {
      timer.sr = document.createElement('div');
      timer.sr.className = 'tlsc-sr';
      timer.sr.setAttribute('aria-live', 'polite');
      timer.sr.setAttribute('aria-atomic', 'true');
      timer.sr.setAttribute('data-tlsc-timer-say', '');
      panel.appendChild(timer.sr);
    }
    window.addEventListener('storage', function (e) {
      // another tab started / restarted / cleared the timer: read the cart fresh before deciding anything here (merged
      // with the 'tlsc-cart-changed' event of the same change into one request)
      if (e.key === TIMER_KEY) otherTabChanged();
    });
  }
  function timerUpdate() {
    if (!timer.cfg) return;
    var now = Date.now();
    if (!state.cart || state.stale) {
      // the cart is not known to be current (other tab, back button): keep counting what is shown, decide nothing
      var st = timerRead();
      if (timer.last && timer.last.view !== 'none' && st && typeof st.s === 'number') {
        var left = Math.max(0, st.s + timer.cfg.dur - now);
        timerRender({ view: left > 0 ? 'run' : 'expired', left: left });
      }
      return;
    }
    var active = timer.test || TimerCore.inWindow(now, timer.cfg.start, timer.cfg.end);
    var sig = TimerCore.signature(state.cart, state.protId);
    var r = TimerCore.step(timerRead(), { sig: sig, now: now, dur: timer.cfg.dur, active: active, visible: state.open,
      prevSig: timer.prevSig, prevView: timer.prevView, leftMs: timer.leftMs });
    if (r.usedLeft) timer.leftMs = null;
    if (r.write) timerWrite(r.store);
    timer.prevSig = sig;
    timer.prevView = r.view;
    if (r.say) timer.say = r.say;
    timer.last = r;
    timerRender(r);
    timerTick(r);
  }
  function timerRender(r) {
    var live = $('[data-tlsc-live]');
    if (!live) return;
    var bar = live.querySelector('[data-tlsc-timer]');
    if (r.view === 'none') {
      if (bar) bar.parentNode.removeChild(bar);
      timer.say = null;
      return;
    }
    if (!bar) {
      var head = live.querySelector('.tlsc__header');
      if (!head) return;
      var run = TimerCore.fill(timer.cfg.text);
      bar = document.createElement('div');
      bar.className = 'tlsc-timer';
      bar.setAttribute('data-tlsc-timer', '');
      bar.setAttribute('role', 'timer');
      bar.setAttribute('aria-live', 'off');
      bar.setAttribute('aria-atomic', 'true');
      // both texts share one grid cell: the bar keeps the height of the longer one, so expiry never moves the drawer
      bar.innerHTML = CLOCK + '<span class="tlsc-timer__msg">' +
        '<span class="tlsc-timer__run">' + esc(run[0]) + '<span class="tlsc-timer__time" data-tlsc-timer-time></span>' + esc(run[1]) + '</span>' +
        '<span class="tlsc-timer__exp">' + esc(timer.cfg.expired) + '</span></span>';
      head.parentNode.insertBefore(bar, head.nextSibling);
    }
    bar.classList.toggle('is-expired', r.view === 'expired');
    var t = bar.querySelector('[data-tlsc-timer-time]');
    var txt = TimerCore.fmt(r.left);
    if (t && t.textContent !== txt) t.textContent = txt;
    if (timer.say && state.open) timerSay(r);
  }
  function timerSay(r) {
    var c = timer.cfg;
    var msg = c.expired;
    if (timer.say === 'start') {
      var mins = Math.max(1, Math.ceil(r.left / 60000));
      msg = TimerCore.fill(c.text).join(mins + ' ' + (mins === 1 ? c.unit1 : c.unit));
    }
    timer.say = null;
    if (!timer.sr) return;
    clearTimeout(timer.sayTimer);
    timer.sr.textContent = '';
    // a moment after the drawer is shown, so a screen reader reads it after the dialog itself
    timer.sayTimer = setTimeout(function () { timer.sr.textContent = msg; }, 400);
  }
  // one 1-second interval at most, only while the countdown runs, the drawer is open and the tab is visible
  function timerTick(r) {
    var on = !!(r && r.view === 'run' && state.open && !document.hidden);
    if (!on) {
      clearTimeout(timer.al);
      clearInterval(timer.iv);
      timer.al = timer.iv = null;
      return;
    }
    if (timer.al || timer.iv) return;
    // first wait for the next whole second of the countdown, then tick every second on that boundary
    timer.al = setTimeout(function () {
      timer.al = null;
      timer.iv = setInterval(timerUpdate, 1000);
      timerUpdate();
    }, TimerCore.untilNextSecond(r.left));
  }
  function timerInfo() {
    return { on: !!timer.cfg, test: timer.test, view: timer.last ? timer.last.view : 'none',
      left: timer.last ? timer.last.left : 0, stored: timer.cfg ? timerRead() : null, ticking: !!(timer.al || timer.iv) };
  }
  /* tlsc-timer-ui:end */

  // ---------- open / close ----------
  function open() {
    if (!el || state.open) return;
    state.open = true;
    state.lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('tlsc-lock');
    timerUpdate();
    setTimeout(function () {
      var c = $('.tlsc__close');
      if (c) { try { c.focus({ preventScroll: true }); } catch (e) { c.focus(); } }
    }, 60);
  }
  function close() {
    if (!el || !state.open) return;
    state.open = false;
    // closed while the checkout button waited for a change (max 3 s): that checkout is cancelled (review 27 Oct)
    if (state.leaving) leaveDone();
    timerTick(null);
    hideUndo();
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('tlsc-lock');
    if (state.onCartPage) { window.location.href = ROOT; return; }
    if (state.lastFocus && state.lastFocus.focus) { try { state.lastFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ } }
  }
  // After an add-to-cart: first bring the drawer fully up to date (new product + shipping protection in one request),
  // then slide it in once - no stale content first, no second redraw. On a slow connection it opens after 1.2 s anyway.
  function adding(on) { document.documentElement.classList.toggle('tlsc-adding', on); }
  function openAfterAdd() {
    var shown = false;
    var slow = setTimeout(function () { shown = true; adding(false); open(); setBusy(true); }, 1200);
    return syncCart(true).catch(function () { /* show what we have */ })
      .then(function () { return state.fixing; })   // a running protection repair finishes before the drawer shows
      .catch(function () { /* show what we have */ })
      .then(function () {
        clearTimeout(slow);
        adding(false);
        if (shown) setBusy(false);
        else open();
        stopWaiting();
      });
  }
  // Cart icon, /cart page, TLSCart.open(): the drawer as rendered with the page is shown right away and checked
  // quietly in the background (other tab, back button) - it is only redrawn when something actually changed.
  function openNow() {
    renderRecommendations();
    open();
    if (document.documentElement.classList.contains('tlsc-adding')) {
      // an add-to-cart is still on its way: wait for it (openAfterAdd brings the drawer up to date), no second sync.
      // That wait counts as a change on its way (review 27 Oct): a checkout tap meanwhile waits for it (max 3 s) instead
      // of going on at once without the product that is being added. Never longer than LIMIT (no answer at all).
      if (!state.waitAdd) {
        state.waitAdd = true;
        setBusy(true);
        track(new Promise(function (resolve) {
          state.waitDone = resolve;
          state.waitCap = setTimeout(function () { addFailed('none'); }, LIMIT);
        }));
      }
      return Promise.resolve();
    }
    return syncCart(false, true).catch(function () { /* keep what we have */ });
  }
  function stopWaiting() {
    if (!state.waitAdd) return;
    state.waitAdd = false;
    clearTimeout(state.waitCap);
    if (state.waitDone) { state.waitDone(); state.waitDone = null; }
    setBusy(false);
  }
  // the add-to-cart failed (or never answered): undo the waiting state and show the cart as it is. err = a failure
  // answer (or no connection) while the open drawer waited for it: counted + our message, so a checkout tap that waited
  // for it stays in the drawer (the product is not in the cart); 'none' = no answer within LIMIT: just stop waiting
  function addFailed(err) {
    adding(false);
    if (!state.waitAdd) return;
    if (err !== 'none') { failed(); showError(err); }
    stopWaiting();
    fetchSection(true).catch(function () { /* keep what we have */ });
  }
  // a failed add-to-cart answer (status) -> the kind of message (busy / error), like NetCore.failure
  function addError(status) {
    return { kind: !status ? 'network' : status === 429 || status >= 500 ? 'busy' : 'error' };
  }
  function scheduleOpen() {
    clearTimeout(state.openTimer);
    state.openTimer = setTimeout(openAfterAdd, 60);
  }

  // ---------- add-to-cart detection (same approach as Upcart) ----------
  function isAddUrl(url) {
    return /\/cart\/add(\.js|\.json)?(\?|$)/.test(String(url || ''));
  }
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var p = nativeFetch(input, init);
    if (isAddUrl(url)) {
      adding(true);   // from the click until the drawer is open: add buttons dimmed (also the sticky bar's)
      state.mut++;
      p.then(function (r) { if (r && r.ok) { signal(); scheduleOpen(); } else addFailed(addError(r && r.status)); })
        .catch(function () { addFailed(addError(0)); });
    }
    return p;
  };
  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__tlscAdd = isAddUrl(url);
    return xhrOpen.apply(this, arguments);
  };
  XMLHttpRequest.prototype.send = function () {
    if (this.__tlscAdd) {
      adding(true);
      state.mut++;
      this.addEventListener('loadend', function () {
        if (this.status >= 200 && this.status < 300) { signal(); scheduleOpen(); }
        else addFailed(addError(this.status));
      });
    }
    return xhrSend.apply(this, arguments);
  };

  // The theme shows a small "added" popdown after add-to-cart; we open the drawer instead.
  window.addEventListener('theme:cart:popdown', function (e) { e.stopImmediatePropagation(); }, true);

  // Header cart icon / any link to /cart opens the drawer.
  document.addEventListener('click', function (e) {
    if (!el || e.defaultPrevented || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var t = e.target && e.target.closest ? e.target.closest('[data-drawer-toggle="drawer-cart"], a[href="/cart"], a[href$="/cart"]') : null;
    if (!t || el.contains(t)) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    openNow();
  }, true);

  // ---------- events inside the drawer ----------
  function bindDrawer() {
    el.addEventListener('click', function (e) {
      var t = e.target;
      if (t.closest('[data-tlsc-close]')) { e.preventDefault(); close(); return; }

      var co = t.closest('[data-tlsc-checkout]');
      if (co) { checkoutClick(e, co); return; }

      if (t.closest('[data-tlsc-undo-btn]')) { undoRemove(); return; }

      if (t.closest('[data-tlsc-continue]')) {
        // F5 empty drawer: keep shopping; on the /cart page there is nothing behind the drawer -> all products
        if (state.onCartPage) window.location.href = ROOT + 'collections/all';
        else close();
        return;
      }

      var bb = t.closest('[data-tlsc-bundle]');
      if (bb) { bundleClick(bb); return; }

      var setBtn = t.closest('[data-tlsc-set]');
      if (setBtn) {
        var item = setBtn.closest('[data-key]');
        if (item) changeLine(item.getAttribute('data-key'), parseInt(setBtn.getAttribute('data-tlsc-set'), 10));
        return;
      }

      var add = t.closest('[data-tlsc-add]');
      if (add) {
        var tileEl = add.closest('[data-tlsc-product]');
        var where = add.closest('[data-tlsc-empty-recs]') ? 'cart-empty' : 'cart-upsell';
        addVariant(add.getAttribute('data-tlsc-add'), where + ':' + ((tileEl && tileEl.getAttribute('data-tlsc-src')) || 'related'));
        return;
      }

      var arrow = t.closest('[data-tlsc-car]');
      if (arrow) {
        var trackEl = arrow.parentNode.querySelector('[data-tlsc-track]');
        var dir = parseInt(arrow.getAttribute('data-tlsc-car'), 10);
        var max = trackEl.scrollWidth - trackEl.clientWidth;
        var next = trackEl.scrollLeft + dir * trackEl.clientWidth;
        if (next > max + 2) next = 0;
        if (next < -2) next = max;
        trackEl.scrollTo({ left: next, behavior: 'smooth' });
      }
    });
    el.addEventListener('change', function (e) {
      var t = e.target;
      if (t.matches('[data-tlsc-protection]')) {
        var attrs = {};
        if (t.checked) {
          attrs[OPTOUT] = '';
          // clear the opt-out without redrawing, then one add that redraws the drawer
          setBusy(true);
          state.mut++;
          // the whole switch-on (both requests) counts as one change on its way: checkout waits for it (F2)
          track(postJSON(ROOT + 'cart/update.js', { attributes: attrs })
            .catch(function () { /* still add it */ })
            .then(function () { return addVariant(t.getAttribute('data-variant'), 'cart-addon:protection'); })
            .then(function (ok) { setBusy(false); if (!ok) protSwitchSync(); return ok; }));
        } else if (t.getAttribute('data-key')) {
          // off = every protection line out (an older cart can hold two), remembered for this cart: never re-added
          var upd = {};
          upd[t.getAttribute('data-key')] = 0;
          ((state.cart && state.cart.items) || []).forEach(function (i) {
            if (String(i.product_id) === state.protId) upd[i.key] = 0;
          });
          attrs[OPTOUT] = '1';
          updateCart({ updates: upd, attributes: attrs }).then(function (ok) { if (!ok) protSwitchSync(); });
        }
        return;
      }
      if (t.matches('[data-tlsc-qty-input]')) {
        var item = t.closest('[data-key]');
        var q = parseInt(t.value, 10);
        if (item && !isNaN(q)) changeLine(item.getAttribute('data-key'), q);
        return;
      }
      if (t.matches('[data-tlsc-variant]')) {
        var tile = t.closest('[data-tlsc-product]');
        var p = state.products[tile.getAttribute('data-tlsc-product')];
        var v = p && p.variants.filter(function (x) { return String(x.id) === t.value; })[0];
        if (!v) return;
        tile.querySelector('[data-tlsc-add]').setAttribute('data-tlsc-add', v.id);
        tile.querySelector('[data-tlsc-prices]').innerHTML = pricesHTML(v);
        var src = (v.featured_image && v.featured_image.src) || p.featured_image;
        if (src) tile.querySelector('.tlsc-up__img img').src = imgUrl(src, 200);
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.open) close();
    });
  }

  function init() {
    el = document.getElementById('tls-cart');
    if (!el) return;
    if (el.parentNode !== document.body) document.body.appendChild(el.closest('.shopify-section') || el);
    moneyFormat = el.getAttribute('data-money-format') || moneyFormat;
    state.protId = el.getAttribute('data-protection-id') || '';
    state.protVariant = el.getAttribute('data-protection-variant') || '';
    state.protDefault = el.getAttribute('data-protection-default') === 'true';
    T = {
      add: el.getAttribute('data-t-add') || 'Toevoegen',
      prev: el.getAttribute('data-t-prev') || '',
      next: el.getAttribute('data-t-next') || '',
      error: el.getAttribute('data-t-error') || 'Er ging iets mis, probeer het opnieuw.',
      slow: el.getAttribute('data-t-slow') || '',
      removed: el.getAttribute('data-t-removed') || '',
      undo: el.getAttribute('data-t-undo') || '',
      bundle1: el.getAttribute('data-t-bundle1') || '',
      bundle2: el.getAttribute('data-t-bundle2') || ''
    };
    state.cart = readCart();
    var live = $('[data-tlsc-live]');
    state.lastMarkup = live ? visibleMarkup(live) : '';
    bindDrawer();
    bundleInit();
    bundleRender();
    // the cart may change elsewhere (other tab, back button restores an old page): read it again when the page is
    // shown again, and until then never decide on the old copy. A page that loaded in a background tab or came
    // from Back starts out unverified.
    var nav = (window.performance && performance.getEntriesByType && performance.getEntriesByType('navigation')[0]) || {};
    state.stale = document.visibilityState !== 'visible' || nav.type === 'back_forward';
    if (state.stale && !document.hidden) fetchSection(true).catch(function () { /* next action reads it fresh */ });
    window.addEventListener('storage', function (e) {
      if (e.key === 'tlsc-cart-changed') otherTabChanged();
    });
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { state.stale = true; timerTick(null); return; }
      if (state.stale) fetchSection(true).catch(function () { /* next action reads it fresh */ });
    });
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      state.stale = true;
      adding(false);
      // back from the checkout (page restored from the back-forward cache): the checkout button works normally again
      leaveDone();
      fetchSection(true).catch(function () { /* next action reads it fresh */ });
    });
    // "cart reserved" countdown (NL/DE November 2026): set up and decided on the cart this page was rendered with
    timerInit();
    timerUpdate();
    // repair carts left with a double / orphan protection by older versions of this drawer
    fixProtection();
    // recommendations are loaded on first open (every open re-renders the section), not on every page view

    // the early inline click catcher (layout) hands over from here on
    window.__tlscReady = true;
    // on the theme's /cart page (back-from-checkout link, reminder mails, very early icon click): show the drawer
    // instead of the plain cart page; closing it continues shopping on the homepage
    state.onCartPage = document.body.classList.contains('template-cart');
    if (state.onCartPage || window.__tlscOpen) {
      window.__tlscOpen = false;
      openNow();
    }
  }

  window.TLSCart = { open: openNow, close: close, refresh: fetchSection, timer: timerInfo,
    // read-only state for checks: pending cart changes, bundle list status
    info: function () { return { pending: pending.count(), fails: state.fails, leaving: !!state.leaving, waitAdd: !!state.waitAdd,
      bundle: { on: bundle.on, products: Object.keys(bundle.data).length, off: Object.keys(bundle.off) } }; } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
